import type { I18nService } from '@core/i18n-legacy';
import { DatasetBase, Dimension, ValueSign } from '@core/reports-types';
import {
  NET_CHANGE_NEGATIVE_COLUMN,
  NET_CHANGE_NET_COLUMN,
  NET_CHANGE_POSITIVE_COLUMN,
  datasetValueFormatterOptions,
} from '@core/reports-util';
import { SupportedNumberFormat, valueFormatter } from '@core/utils-legacy';

export interface NetChangeRow {
  dimension: Dimension;
  positive: number;
  negative: number;
  net: number;
}

const toNumber = (value: unknown): number =>
  typeof value === 'number' ? value : Number(value ?? 0);

/**
 * Reads one result row as a net change triplet. Net comes from the backend
 * computed column so the definition of "net" lives in one place; it is derived
 * on the fly only when that column is missing.
 */
export const readNetChangeRow = (dataset: DatasetBase, rowIndex: number): NetChangeRow => {
  const row = dataset.getRow(rowIndex + 1) ?? [];
  const positive = toNumber(row[NET_CHANGE_POSITIVE_COLUMN]);
  const negative = toNumber(row[NET_CHANGE_NEGATIVE_COLUMN]);

  return {
    dimension: <Dimension>row[0],
    positive,
    negative,
    net:
      row.length > NET_CHANGE_NET_COLUMN
        ? toNumber(row[NET_CHANGE_NET_COLUMN])
        : positive - negative,
  };
};

/** Label of the backend computed net column, empty when it is not returned. */
export const netColumnName = (dataset: DatasetBase): string =>
  <string>(<Dimension | undefined>dataset.getDataAt([0, NET_CHANGE_NET_COLUMN]))?.name ?? '';

export const netChangeSign = (net: number): ValueSign | undefined => {
  if (net > 0) return 'positive';
  if (net < 0) return 'negative';
  return undefined;
};

export const formatNetChangeValue = (
  value: number,
  dataset: DatasetBase,
  i18n: I18nService,
): string =>
  valueFormatter(value, datasetValueFormatterOptions(dataset), i18n, {
    number: { format: SupportedNumberFormat.IntlStandard },
  });

/** Net is always shown with an explicit sign so the direction reads at a glance. */
export const formatNet = (net: number, dataset: DatasetBase, i18n: I18nService): string =>
  `${net > 0 ? '+' : ''}${formatNetChangeValue(net, dataset, i18n)}`;
